import { resetUnreadCount } from './conversation';
import { subscribeFaye } from './faye';

import * as AppStateActions from '../actionTypes/app-state';
import { observable } from '../utils/events';
import { isChannelLinked } from '../utils/user';
import { getIntegration, getActiveConversation } from '../utils/app';
import { CHANNEL_DETAILS } from '../constants/channels';
import { WIDGET_STATE } from '../constants/app';
import { CONNECTION_STATE } from '../constants/connection-state';

// TODO R2: abstract typingTimers to its own class
const typingTimers = {};

function generateTimerKey(role, id) {
    if (role === 'appMaker') {
        return role;
    }

    return `${role}:${id}`;
}

export function toggleWidget() {
    return (dispatch, getState) => {
        const { widgetState } = getState().appState;
        if (widgetState !== WIDGET_STATE.EMBEDDED) {
            if (widgetState === WIDGET_STATE.OPENED) {
                return dispatch(closeWidget());
            }
            return dispatch(openWidget());
        }
    };
}

export function openWidget() {
    return (dispatch, getState) => {
        const { widgetState } = getState().appState;
        if (widgetState !== WIDGET_STATE.EMBEDDED) {
            dispatch({
                type: AppStateActions.OPEN_WIDGET,
            });
            observable.trigger('widget:opened');
            dispatch(resetUnreadCount());
        }
    };
}

export function closeWidget() {
    return (dispatch, getState) => {
        const { widgetState } = getState().appState;
        if (widgetState !== WIDGET_STATE.EMBEDDED) {
            dispatch({
                type: AppStateActions.CLOSE_WIDGET,
            });
            observable.trigger('widget:closed');
            dispatch(resetUnreadCount());
        }
    };
}

export function setActiveConversationId(id) {
    return {
        type: AppStateActions.SET_ACTIVE_CONVERSATION_ID,
        id,
    };
}

export function showUpload() {
    return {
        type: AppStateActions.SHOW_UPLOAD,
    };
}

export function hideUpload() {
    return {
        type: AppStateActions.HIDE_UPLOAD,
    };
}

export function showSettings() {
    return {
        type: AppStateActions.SHOW_SETTINGS,
    };
}

export function setInitializationState(value) {
    return {
        type: AppStateActions.SET_INITIALIZATION_STATE,
        value,
    };
}

export function hideSettings() {
    return {
        type: AppStateActions.HIDE_SETTINGS,
    };
}

export function enableImageUpload() {
    return {
        type: AppStateActions.ENABLE_IMAGE_UPLOAD,
    };
}

export function disableImageUpload() {
    return {
        type: AppStateActions.DISABLE_IMAGE_UPLOAD,
    };
}

export function enableSoundNotification() {
    return {
        type: AppStateActions.ENABLE_SOUND_NOTIFICATION,
    };
}

export function disableSoundNotification() {
    return {
        type: AppStateActions.DISABLE_SOUND_NOTIFICATION,
    };
}

export function setEmailReadonly() {
    return {
        type: AppStateActions.SET_EMAIL_READONLY,
    };
}

export function unsetEmailReadonly() {
    return {
        type: AppStateActions.UNSET_EMAIL_READONLY,
    };
}

export function showConnectNotification(timestamp = Date.now() / 1000.0) {
    return {
        type: AppStateActions.SHOW_CONNECT_NOTIFICATION,
        timestamp,
    };
}

export function hideConnectNotification() {
    return {
        type: AppStateActions.HIDE_CONNECT_NOTIFICATION,
    };
}

export function showErrorNotification(message) {
    return (dispatch) => {
        setTimeout(() => {
            dispatch(hideErrorNotification());
        }, 10000);

        dispatch({
            type: AppStateActions.SHOW_ERROR_NOTIFICATION,
            message,
        });
    };
}

export function hideErrorNotification() {
    return {
        type: AppStateActions.HIDE_ERROR_NOTIFICATION,
    };
}

export function setEmbedded(value) {
    return {
        type: AppStateActions.SET_EMBEDDED,
        value,
    };
}

export function showChannelPage(channelType) {
    return (dispatch, getState) => {
        const {
            user,
            config: { integrations },
        } = getState();
        const channelDetails = CHANNEL_DETAILS[channelType];
        const isLinked = isChannelLinked(user.clients, channelType);
        const appChannel = getIntegration(integrations, channelType);
        const url = channelDetails.getURL(appChannel);
        const openLink = url && (!channelDetails.Component || isLinked);

        if (openLink) {
            window.open(url);
            if (!isLinked && channelDetails.isLinkable) {
                return dispatch(subscribeFaye());
            }
        } else {
            dispatch({
                type: AppStateActions.SHOW_CHANNEL_PAGE,
                channelType,
            });

            return dispatch(channelDetails.onChannelPage());
        }
    };
}

export function hideChannelPage() {
    return {
        type: AppStateActions.HIDE_CHANNEL_PAGE,
    };
}

export function setIntroHeight(value) {
    return {
        type: AppStateActions.SET_INTRO_HEIGHT,
        value,
    };
}

export function disableAnimation() {
    return {
        type: AppStateActions.DISABLE_ANIMATION,
    };
}

export function setFetchingMoreMessages(value) {
    return {
        type: AppStateActions.SET_FETCHING_MORE_MESSAGES,
        value,
    };
}

export function setShouldScrollToBottom(value) {
    return {
        type: AppStateActions.SET_SHOULD_SCROLL_TO_BOTTOM,
        value,
    };
}

export function addTypingIndicatorEvent(data = {}, role, id) {
    const { avatarUrl, name } = data;
    return (dispatch) => {
        const key = generateTimerKey(role, id);

        if (typingTimers[key]) {
            clearTimeout(typingTimers[key]);
        }

        typingTimers[key] = setTimeout(() => {
            dispatch(removeTypingIndicatorEvent(role, id));
        }, 10 * 1000);

        dispatch({
            type: AppStateActions.ADD_TYPING_EVENT,
            typingEvent: {
                role,
                id,
                typerInfo: {
                    avatarUrl,
                    name,
                },
            },
        });
    };
}

export function removeTypingIndicatorEvent(role, id) {
    return (dispatch) => {
        const key = generateTimerKey(role, id);

        if (typingTimers[key]) {
            clearTimeout(typingTimers[key]);
            delete typingTimers[key];
        }

        dispatch({
            type: AppStateActions.REMOVE_TYPING_EVENT,
            typingEvent: {
                role,
                id,
            },
        });
    };
}

export function cleanTypingIndicatorEvents() {
    Object.keys(typingTimers).forEach((key) => {
        clearTimeout(typingTimers[key]);
        delete typingTimers[key];
    });

    return {
        type: AppStateActions.CLEAN_TYPING_EVENTS,
    };
}

export function updateWidgetSize(size) {
    return {
        type: AppStateActions.UPDATE_WIDGET_SIZE,
        size,
    };
}

export function setUserIsTyping(isTyping) {
    return {
        isTyping,
        type: AppStateActions.SET_USER_IS_TYPING,
    };
}

let wasEverConnected = false;

export function setConnectionState(connectionState) {
    return (dispatch, getState) => {
        const wasConnected = getState().appState.connectionState === CONNECTION_STATE.CONNECTED;

        switch (connectionState) {
            case CONNECTION_STATE.CONNECTED:
                if (!wasConnected || !wasEverConnected) {
                    observable.trigger('connected', { conversation: getActiveConversation() });
                }
                wasEverConnected = true;
                break;
            case CONNECTION_STATE.DISCONNECTED:
            case CONNECTION_STATE.DISCONNECTED_WITH_RETRY:
                dispatch(cleanTypingIndicatorEvents());

                if (wasConnected) {
                    observable.trigger('disconnected', { conversation: getActiveConversation() });
                }
                break;
        }

        dispatch({
            type: AppStateActions.SET_CONNECTION_STATE,
            connectionState,
        });
    };
}

export function setBlockChatInput(isBlocked) {
    return {
        type: AppStateActions.SET_BLOCK_CHAT_INPUT,
        isBlocked,
    };
}
